import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Leaf, Users, ChefHat, Star, ArrowRight, Instagram, Quote as QuoteIcon } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { CONTACT } from "@/data/site";
import Seo from "@/components/Seo";

const HERO = "https://images.unsplash.com/photo-1525441273400-056e9c7517b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const WHY = [
  { icon: Leaf, title: "Fresh, Local Ingredients", text: "Sourced daily from Southwest Florida's finest purveyors for peak flavor and quality." },
  { icon: ChefHat, title: "Culinary Craftsmanship", text: "Chef-driven menus blending classic technique with bold, modern creativity." },
  { icon: Award, title: "White-Glove Service", text: "Professional staff and flawless execution that let you enjoy your own event." },
  { icon: Users, title: "Personalized Experience", text: "Every menu is tailored to your vision, taste, and guests — never off the shelf." },
];

const PROCESS = [
  { n: "01", title: "Consultation", text: "Share your vision, guest count, and taste preferences." },
  { n: "02", title: "Custom Menu", text: "We craft a bespoke menu and transparent quote." },
  { n: "03", title: "Tasting", text: "Sample your selections and refine every detail." },
  { n: "04", title: "The Experience", text: "We handle setup, service & cleanup. You savor the moment." },
];

const INSTA = [
  "https://images.unsplash.com/photo-1616671285410-2a676a9a433d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1574862612978-33bdb5f6a334?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1673912402587-57ac40f1b4a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1665607437981-973dcd6a22bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1676471926534-d5c9771909fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1527751171053-6ac5ec50000b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];

export default function Home() {
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="K.O CHEF & CATERING | Premium Catering & Private Chef — Naples, Fort Myers & SW Florida"
        description="Premium catering & private chef services in Naples, Bonita Springs & Fort Myers. Weddings, corporate events, private dinners and luxury events across Southwest Florida. Request a free quote today."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.5, ease: "easeOut" }} className="absolute inset-0 z-0">
          <img src={HERO} alt="Exceptional catering and private chef experience by K.O Chef & Catering in Southwest Florida" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-black/40" />
        </motion.div>
        <div className="container-lux relative z-10 pt-24">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="overline mb-6">Southwest Florida · Est. Excellence</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.9 }} className="text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[1.05] max-w-4xl">
            Exceptional Catering & <span className="italic text-gold">Private Chef</span> Experiences
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }} className="mt-8 text-lg md:text-xl text-white/70 max-w-xl font-light">
            From intimate dinners to luxury weddings and corporate events.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="mt-12 flex flex-col sm:flex-row gap-5">
            <Link to="/request-a-quote" className="btn-gold" data-testid="hero-quote-btn">Request a Quote</Link>
            <Link to="/build-your-menu" className="btn-outline" data-testid="hero-build-btn">Build Your Menu</Link>
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-background" data-testid="why-us">
        <div className="container-lux">
          <div className="max-w-2xl mb-16">
            <Reveal><p className="overline mb-4">Why Choose Us</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-4xl md:text-5xl font-light text-white">Hospitality, elevated to an art form.</h2></Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div className="bg-background p-8 h-full group hover:bg-white/[0.02] transition-colors">
                  <w.icon className="w-8 h-8 text-gold mb-6" strokeWidth={1.3} />
                  <h3 className="text-xl font-light text-white mb-3">{w.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PACKAGES */}
      <section className="py-24 md:py-32 bg-background" data-testid="featured-packages">
        <div className="container-lux">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-xl">
              <Reveal><p className="overline mb-4">Featured Menu Packages</p></Reveal>
              <Reveal delay={0.1}><h2 className="text-4xl md:text-5xl font-light text-white">Curated for every occasion.</h2></Reveal>
            </div>
            <Reveal delay={0.2}><Link to="/packages" className="text-gold text-sm uppercase tracking-widest flex items-center gap-2 hover:gap-4 transition-all">View all packages <ArrowRight className="w-4 h-4" /></Link></Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <div className={`border p-8 h-full flex flex-col ${p.featured ? "border-gold bg-gold/[0.04]" : "border-white/10"}`}>
                  {p.featured && <span className="overline text-gold mb-4">Most Popular</span>}
                  <h3 className="text-2xl font-light text-white">{p.name}</h3>
                  <p className="text-sm text-white/55 mt-3 mb-6 leading-relaxed">{p.description}</p>
                  <div className="mt-auto">
                    <p className="text-3xl font-serif text-gold">${p.price_per_person}<span className="text-sm text-white/40"> / person</span></p>
                    <p className="text-xs text-white/40 mt-1 mb-6">Minimum {p.min_guests} guests</p>
                    <Link to="/request-a-quote" className="btn-outline w-full !py-3" data-testid={`home-pkg-${i}`}>Request Quote</Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32 bg-background" data-testid="process">
        <div className="container-lux">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal><p className="overline mb-4">Our Process</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-4xl md:text-5xl font-light text-white">From vision to unforgettable.</h2></Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {PROCESS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="text-center md:text-left">
                  <p className="font-serif text-6xl text-gold/25">{s.n}</p>
                  <h3 className="text-xl font-light text-white mt-4 mb-3">{s.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 md:py-32 bg-background" data-testid="home-reviews">
        <div className="container-lux">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal><p className="overline mb-4">Customer Reviews</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-4xl md:text-5xl font-light text-white">Loved across Southwest Florida.</h2></Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <div className="border border-white/10 p-8 h-full flex flex-col">
                  <QuoteIcon className="w-8 h-8 text-gold/40 mb-5" />
                  <div className="flex gap-1 mb-4">{Array.from({length:r.rating}).map((_,k)=><Star key={k} className="w-4 h-4 fill-gold text-gold" />)}</div>
                  <p className="text-white/70 leading-relaxed italic font-serif">"{r.text}"</p>
                  <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
                    <img src={r.photo} alt={r.name} className="w-11 h-11 rounded-full object-cover" />
                    <div><p className="text-white text-sm">{r.name}</p><p className="text-white/40 text-xs">{r.role}</p></div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="py-24 md:py-32 bg-background" data-testid="instagram-feed">
        <div className="container-lux text-center mb-12">
          <Reveal><p className="overline mb-4">Follow Along</p></Reveal>
          <Reveal delay={0.1}><a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 text-3xl md:text-4xl font-light text-white hover:text-gold transition-colors"><Instagram className="w-7 h-7 text-gold" />{CONTACT.instagramHandle}</a></Reveal>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-white/5">
          {INSTA.map((src, i) => (
            <a key={i} href={CONTACT.instagram} target="_blank" rel="noreferrer" className="relative aspect-square group overflow-hidden" data-testid={`insta-${i}`}>
              <img src={src} alt={`K.O Chef & Catering gourmet catering in Naples, Fort Myers & Southwest Florida ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Instagram className="w-6 h-6 text-gold" /></div>
            </a>
          ))}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="contact-cta">
        <div className="absolute inset-0 z-0"><img src="https://images.unsplash.com/photo-1555244162-803834f70033?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Elegant catered dinner event by K.O Chef & Catering in Southwest Florida" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/75" /></div>
        <div className="container-lux relative z-10 text-center">
          <Reveal><h2 className="text-4xl md:text-6xl font-light text-white max-w-3xl mx-auto leading-tight">Let's create something <span className="italic text-gold">unforgettable</span>.</h2></Reveal>
          <Reveal delay={0.15}><p className="text-white/60 mt-6 max-w-xl mx-auto">Tell us about your event and receive a personalized quote within 24 hours.</p></Reveal>
          <Reveal delay={0.3}><div className="mt-10 flex flex-col sm:flex-row gap-5 justify-center"><Link to="/request-a-quote" className="btn-gold" data-testid="cta-quote">Request a Quote</Link><Link to="/contact" className="btn-outline" data-testid="cta-contact">Contact Us</Link></div></Reveal>
        </div>
      </section>
    </div>
  );
}
